/* ============================================================
   ZENITH APPAREL — Global JS
   ============================================================ */

/* ---- Scroll-reveal ---- */
if (document.body.classList.contains('animate--on-scroll')) {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

  document.querySelectorAll('.animate').forEach(el => observer.observe(el));
}

/* ---- Header scroll state ---- */
const header = document.getElementById('SiteHeader');
if (header) {
  const headerScroll = () => {
    header.classList.toggle('scrolled', window.scrollY > 10);
  };
  window.addEventListener('scroll', headerScroll, { passive: true });
  headerScroll();
}

/* ---- Cart count update (used by cart.js) ---- */
window.updateCartCount = function(count) {
  const countEl = document.getElementById('CartCount');
  const mobileEl = document.getElementById('CartCountMobile');
  const drawerCount = document.getElementById('CartDrawerCount');
  if (countEl) {
    countEl.textContent = '(' + count + ')';
    countEl.hidden = count === 0;
  }
  if (mobileEl) {
    mobileEl.textContent = count;
    mobileEl.classList.toggle('hidden', count === 0);
  }
  if (drawerCount) drawerCount.textContent = count;
};

/* ---- Fetch helpers ---- */
window.fetchCart = function() {
  return fetch('/cart.js').then(r => r.json());
};

/* `properties` is optional. Anything the product form collects as
   properties[Name] (the 4-pack's Pick 1–4, a personalised name) must ride along
   here, or it never reaches the cart, the checkout or the order. Posting only
   { id, quantity } silently dropped the 4-pack picks on every order. */

/* Rejections must THROW, exactly as addItemsToCart below does. /cart/add.js
   answers a refusal (sold out, quantity cap, a failed server-side validation
   such as the gift-card recipient fields) with a 4xx and a JSON body carrying
   `description`. Resolving that as success opened the drawer on an unchanged
   cart and told the shopper nothing — the same silent-failure shape as the
   dropped line item properties fixed 15 Sep, in the function that fix missed. */
window.addToCart = function(variantId, quantity = 1, properties) {
  const body = { id: variantId, quantity };
  if (properties && Object.keys(properties).length) body.properties = properties;
  return fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  }).then(async r => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.description || data.message || 'Add to cart failed');
    return data;
  });
};

/* Add several lines in ONE request.

   /cart/add.js accepts an `items` array, which matters here: adding a bundle as
   N sequential POSTs can half-succeed and leave the shopper with part of what
   they ticked and no clear error. One request either takes the lot or none. */
window.addItemsToCart = function(items) {
  return fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items })
  }).then(async r => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.description || data.message || 'Add to cart failed');
    return data;
  });
};

window.updateCartItem = function(key, quantity) {
  return fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity })
  }).then(r => r.json());
};

window.removeCartItem = function(key) {
  return window.updateCartItem(key, 0);
};

/* ---- Cart drawer re-render ----
   Shared by cart.js and product.js so there is one implementation of "get the
   drawer's current HTML and swap it in".

   Two sources, in order:
     1. /cart?sections=cart-drawer — cheap, returns just the section.
     2. /cart — the full page, parsed for the same nodes.

   The fallback exists because the Section Rendering API only resolves sections,
   and it returned {"cart-drawer": null} while the drawer was a snippet. Both
   callers guarded on that value, silently skipped the swap, and left the drawer
   showing "Your cart is empty" until the shopper refreshed. The drawer is now a
   section, but the fallback stays: the drawer must never be wrong, and a single
   extra request on a failed path is cheaper than showing an empty cart to
   someone who just added to it. */
window.getCartDrawerHtml = async function() {
  try {
    const res = await fetch('/cart?sections=cart-drawer');
    if (res.ok) {
      const data = await res.json();
      if (data && data['cart-drawer']) return data['cart-drawer'];
    }
  } catch (e) {}

  try {
    const res = await fetch('/cart');
    if (res.ok) return await res.text();
  } catch (e) {}

  return null;
};

window.refreshCartDrawerDom = async function() {
  const html = await window.getCartDrawerHtml();
  if (!html) return false;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const pairs = [['CartDrawerItems', 'CartDrawerItems'], ['CartDrawerFooter', 'CartDrawerFooter']];
  let swapped = false;

  pairs.forEach(function(ids) {
    const fresh = doc.getElementById(ids[0]);
    const current = document.getElementById(ids[1]);
    if (fresh && current) {
      current.innerHTML = fresh.innerHTML;
      swapped = true;
    }
  });

  return swapped;
};

/* ---- A11y focus trap ---- */
window.trapFocus = function(element) {
  const focusable = element.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  element.addEventListener('keydown', function trap(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });
};

window.removeTrapFocus = function(element) {
  const clone = element.cloneNode(true);
  element.parentNode.replaceChild(clone, element);
};

/* ---- Auto scroll-reveal decoration + stagger ---- */
(function() {
  if (!document.body.classList.contains('animate--on-scroll')) return;
  var groups = [
    '.featured-collection__grid .product-card',
    '.collection-tiles__grid .collection-tile',
    '.product-grid .product-card'
  ];
  groups.forEach(function(sel) {
    document.querySelectorAll(sel).forEach(function(el, i) {
      el.classList.add('animate');
      el.style.setProperty('--reveal-delay', (i % 4) * 80 + 'ms');
    });
  });
  [
    '.featured-collection__header',
    '.collection-tiles__header',
    '.image-with-text__content-inner',
    '.email-signup__inner',
    '.footer__newsletter'
  ].forEach(function(sel) {
    document.querySelectorAll(sel).forEach(function(el) { el.classList.add('animate'); });
  });
  var observer = new IntersectionObserver(function(entries) {
    entries.forEach(function(entry) {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -30px 0px' });
  document.querySelectorAll('.animate:not(.is-visible)').forEach(function(el) { observer.observe(el); });
})();

/* ---- Back to top ---- */
(function() {
  var btn = document.getElementById('BackToTop');
  if (!btn) return;
  btn.hidden = false;
  var toggle = function() {
    btn.classList.toggle('is-visible', window.scrollY > 600);
  };
  window.addEventListener('scroll', toggle, { passive: true });
  toggle();
  btn.addEventListener('click', function() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
})();

/* ---- Cookie settings (Shopify Customer Privacy API if available) ---- */
(function() {
  document.querySelectorAll('[data-cookie-settings]').forEach(function(btn) {
    btn.addEventListener('click', function() {
      if (window.Shopify && window.Shopify.customerPrivacy && window.Shopify.customerPrivacy.openPreferences) {
        window.Shopify.customerPrivacy.openPreferences();
      } else if (window.privacyBanner && window.privacyBanner.showPreferences) {
        window.privacyBanner.showPreferences();
      } else {
        window.location.href = '/pages/cookie-policy';
      }
    });
  });
})();
